/**
 * 数字员工智能体注册表
 * 新增智能体：在此追加配置并添加对应路由与页面组件即可。
 */

import { isQuickEcomMenuEnabled } from '@/constants/digitalEmployeeMenu'

export const DE_AGENT_GROUPS = {
  main: 'main',
  asset: 'asset',
}

/**
 * 快捷直达类智能体：点击后跳转至独立路由，无需创建项目
 * @property {string} quickRoute 目标路由名
 */
export const DE_QUICK_AGENTS = [
  {
    id: 'quick-ecom',
    label: '快捷出图',
    group: DE_AGENT_GROUPS.main,
    routeName: 'DeAgentQuickEcom',
    quickRoute: 'QuickEcom',
    path: 'quick-ecom',
    icon: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>',
    component: () => import('./agents/QuickEcomAgentView.vue'),
  },
]

/** 工作流类智能体（创建项目后进入 workflows/ 下对应页面） */
export const DE_WORKFLOW_AGENTS = [
  {
    id: 'ecom',
    label: '电商生图',
    group: DE_AGENT_GROUPS.main,
    routeName: 'DeAgentEcom',
    path: 'ecom',
    workflowRouteName: 'EcomWorkflow',
    icon: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 01-8 0"/></svg>',
    component: () => import('./agents/EcomAgentView.vue'),
  },
  {
    id: 'general',
    label: '通用生图',
    group: DE_AGENT_GROUPS.main,
    routeName: 'DeAgentGeneral',
    path: 'general',
    workflowRouteName: 'GeneralWorkflow',
    icon: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>',
    component: () => import('./agents/GeneralAgentView.vue'),
  },
  {
    id: 'video',
    label: '创意视频',
    group: DE_AGENT_GROUPS.main,
    routeName: 'DeAgentVideo',
    path: 'video',
    workflowRouteName: 'VideoWorkflow',
    icon: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/></svg>',
    component: () => import('./agents/VideoAgentView.vue'),
  },
]

/** 资产 / 灵感类智能体（Hub 内直接交互，无独立工作流页） */
export const DE_UTILITY_AGENTS = [
  {
    id: 'assets',
    label: '员工资产',
    group: DE_AGENT_GROUPS.asset,
    routeName: 'DeAgentAssets',
    path: 'assets',
    sub: false,
    icon: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>',
    component: () => import('./agents/AssetsAgentView.vue'),
  },
  {
    id: 'inspiration',
    label: '灵感广场',
    group: DE_AGENT_GROUPS.asset,
    routeName: 'DeAgentInspiration',
    path: 'inspiration',
    sub: true,
    icon: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/></svg>',
    component: () => import('./agents/InspirationAgentView.vue'),
  },
]

/** 按菜单配置过滤后的快捷智能体（生产打包默认不含「快捷出图」） */
export function getVisibleQuickAgents() {
  return isQuickEcomMenuEnabled() ? DE_QUICK_AGENTS : []
}

export function getAllAgents() {
  return [...getVisibleQuickAgents(), ...DE_WORKFLOW_AGENTS, ...DE_UTILITY_AGENTS]
}

export const DE_ALL_AGENTS = getAllAgents()

export const DE_WORKFLOW_AGENT_IDS = DE_WORKFLOW_AGENTS.map((a) => a.id)

export function getQuickAgentIds() {
  return getVisibleQuickAgents().map((a) => a.id)
}

export function isQuickAgent(agentId) {
  return getQuickAgentIds().includes(agentId)
}

export function getQuickAgentRoute(agentId) {
  const agent = getVisibleQuickAgents().find((a) => a.id === agentId)
  return agent?.quickRoute || null
}

export const DE_DEFAULT_AGENT_ID = 'ecom'

export function getAgentById(id) {
  return getAllAgents().find((a) => a.id === id) || null
}

export function getAgentByRouteName(routeName) {
  return getAllAgents().find((a) => a.routeName === routeName) || null
}

export function getWorkflowRouteName(agentId) {
  const agent = getAgentById(agentId)
  return agent?.workflowRouteName || 'EcomWorkflow'
}

export function isWorkflowAgent(agentId) {
  return DE_WORKFLOW_AGENT_IDS.includes(agentId)
}

export function buildAgentHubPath(empId, agentId = DE_DEFAULT_AGENT_ID) {
  const agent = getAgentById(agentId)
  const segment = agent?.path || DE_DEFAULT_AGENT_ID
  return `/digital-employee/use/${empId}/${segment}`
}

/** 供 Hub 侧栏渲染：快捷智能体排在工作流前面 */
export function getNavMainItems() {
  const quick = getVisibleQuickAgents().map(({ id, label, icon, quickRoute }) => ({
    id, label, icon, quickRoute, isQuick: true,
  }))
  const workflow = DE_WORKFLOW_AGENTS.map(({ id, label, icon }) => ({ id, label, icon }))
  return [...quick, ...workflow]
}

export function getNavAssetItems() {
  return DE_UTILITY_AGENTS.map(({ id, label, icon, sub }) => ({ id, label, icon, sub }))
}

export function getAgentRouteName(agentId) {
  const agent = getAgentById(agentId)
  return agent?.routeName || 'DeAgentEcom'
}

/** 生成 Hub 子路由配置（快捷直达类无 component，不注册子路由） */
export function buildAgentChildRoutes() {
  return getAllAgents()
    .filter((agent) => agent.component)
    .map((agent) => ({
      path: agent.path,
      name: agent.routeName,
      component: agent.component,
      meta: { agentId: agent.id, agentLabel: agent.label },
    }))
}
